import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import {
  FISHEYE_VIEW_TYPES,
  FISHEYE_MOUNTING,
} from "../../../Pages/Cameras/components/PlaybackWebRTC/FishEyeGridSettings";
import ImmervisionManager from "./ImmervisionManager";
import { DeviceModel } from "../../Models/devices";
import { FishEyePlayerModel } from "../VideoPlayer/models/FishEyePlayerModel";
import { fishEyeMountingStringToEnum, fishEyeViewStringToEnum } from "../HelperCode";

const useStyles = makeStyles(() =>
  createStyles({
    videoDisplayer: {
      position: "absolute",
      height: "100%",
      width: "100%",
    },
    videoContainer: {
      position: "relative",
    },
    container: { backgroundColor: "rgba(0,0,0,0)" },
    dewarpedContainer: {
      display: "flex",
      height: "100%",
      width: "100%",
    },
    perimeterContainer: {
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%",
      height: "100%",
      backgroundColor: "black",
    },
    perimeterItem: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: "50%",
      display: "flex",
      flexDirection: "column",
    },
    perimeterItemWall: {
      height: "42%",
      width: "100%",
      overflow: "hidden",
    },
    quadContainer: {
      position: "absolute",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr 1fr",
      width: "100%",
      height: "100%",
    },
  })
);

interface Props {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  device: DeviceModel | undefined;
  requestVideoToBeRerendered: () => void;
  fishEyePlayerModel?: FishEyePlayerModel;
}

const DewarpViews = forwardRef(
  ({ videoRef, requestVideoToBeRerendered, fishEyePlayerModel }: Props, ref) => {
    const isFishEye = () => fishEyePlayerModel?.isFishEye === true;
    if (!isFishEye()) {
      return null;
    }
    const [fishEyeView, setFishEyeView] = useState<FISHEYE_VIEW_TYPES>(
      fishEyeViewStringToEnum(fishEyePlayerModel?.view)
    );
    const [immervisionManager, setImmervisionManager] = useState<ImmervisionManager | null>(null);
    const [fishEyeMounting, setFishEyeMounting] = useState<FISHEYE_MOUNTING>(
      fishEyeMountingStringToEnum(fishEyePlayerModel?.mounting)
    );
    const classes = useStyles();
    const singleRef = useRef<HTMLDivElement>(null);
    const singleChildRef = useRef<HTMLDivElement>(null);
    const ePtzRef = useRef<HTMLDivElement>(null);
    const ePtzChildRef = useRef<HTMLDivElement>(null);
    const perimeterRef = useRef<HTMLDivElement>(null);
    const perimeter1Ref = useRef<HTMLDivElement>(null);
    const perimeter2Ref = useRef<HTMLDivElement>(null);
    const quadRef = useRef<HTMLDivElement>(null);
    const quad1Ref = useRef<HTMLDivElement>(null);
    const quad2Ref = useRef<HTMLDivElement>(null);
    const quad3Ref = useRef<HTMLDivElement>(null);
    const quad4Ref = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      changeView(view: FISHEYE_VIEW_TYPES) {
        setFishEyeView(view);
      },
      changeMounting(mountType: FISHEYE_MOUNTING) {
        setFishEyeMounting(mountType);
      },
    }));

    useEffect(() => {
      if (!isFishEye()) {
        return;
      }
      fishEyeSelectViewContainer(fishEyeView);
    }, [fishEyeView]);

    const onVideoWasUpdated = () => {
      if (!videoRef.current || !isFishEye()) {
        return;
      }
      const manager = new ImmervisionManager(fishEyePlayerModel?.mounting);
      setImmervisionManager(manager);
    };

    useEffect(onVideoWasUpdated, [videoRef]);

    const onImmervisionWasUpdated = () => {
      const load = async () => {
        if (!immervisionManager) {
          return;
        }
        await immervisionManager.init();
        fishEyeSelectViewContainer(fishEyeView);
      };
      load();
      return () => {
        immervisionManager?.destroy();
      };
    };

    useEffect(onImmervisionWasUpdated, [immervisionManager]);

    async function createSingleFishEyeView() {
      if (!immervisionManager) {
        return;
      }
      await immervisionManager.loadSingleView(videoRef.current, singleChildRef.current);
      requestVideoToBeRerendered();
    }

    async function createEPtzFishEyeView() {
      if (!immervisionManager) {
        return;
      }
      await immervisionManager.loadEPtzView(videoRef.current, ePtzChildRef.current);
      requestVideoToBeRerendered();
    }

    function createPerimeterFishEyeView() {
      if (!immervisionManager) {
        return;
      }
      immervisionManager.loadPerimeterView(
        videoRef.current,
        perimeter1Ref.current,
        perimeter2Ref.current
      );
      requestVideoToBeRerendered();
    }

    function createQuadFishEyeView() {
      if (!immervisionManager) {
        return;
      }
      immervisionManager.loadQuadView(
        videoRef.current,
        quad1Ref.current,
        quad2Ref.current,
        quad3Ref.current,
        quad4Ref.current
      );
      requestVideoToBeRerendered();
    }

    const fishEyeSelectViewContainer = (option: FISHEYE_VIEW_TYPES) => {
      if (!immervisionManager) {
        return;
      }
      const singleViewContainer = singleRef.current;
      const ePtzViewContainer = ePtzRef.current;
      const perimeterContainer = perimeterRef.current;
      const quadContainer = quadRef.current;
      if (singleViewContainer && option === FISHEYE_VIEW_TYPES.SINGLE) {
        createSingleFishEyeView();
      }
      if (ePtzViewContainer && option === FISHEYE_VIEW_TYPES.E_PTZ) {
        createEPtzFishEyeView();
      }
      if (perimeterContainer && option === FISHEYE_VIEW_TYPES.PERIMETER) {
        createPerimeterFishEyeView();
      }
      if (quadContainer && option === FISHEYE_VIEW_TYPES.QUAD) {
        createQuadFishEyeView();
      }
    };

    useEffect(() => {
      if (fishEyeMounting === FISHEYE_MOUNTING.DEFAULT) {
        return;
      }
      const mounting = fishEyeMounting === FISHEYE_MOUNTING.WALL ? "WALL" : "CEILING";
      immervisionManager?.singleView?.iveViewer.setState({
        orientation: mounting,
      });
      immervisionManager?.perimeterView?.iveViewer.setState({
        orientation: mounting,
      });
      immervisionManager?.quadView?.iveViewer.setState({
        orientation: mounting,
      });
    }, [fishEyeMounting]);

    return (
      <>
        {fishEyeView === FISHEYE_VIEW_TYPES.SINGLE && (
          <div data-testid="single-view" ref={singleRef} className={classes.videoDisplayer}>
            <div
              data-testid="dewarped-single-output"
              ref={singleChildRef}
              className={classes.dewarpedContainer}
            ></div>
          </div>
        )}
        {fishEyeView === FISHEYE_VIEW_TYPES.E_PTZ && (
          <div data-testid="ePtz-view" ref={ePtzRef} className={classes.videoDisplayer}>
            <div
              data-testid="dewarped-single-output2"
              ref={ePtzChildRef}
              className={classes.dewarpedContainer}
            ></div>
          </div>
        )}
        {fishEyeView === FISHEYE_VIEW_TYPES.PERIMETER && (
          <div
            data-testid="perimeter-view"
            ref={perimeterRef}
            className={classes.perimeterContainer}
          >
            <div
              ref={perimeter1Ref}
              className={
                fishEyeMounting === FISHEYE_MOUNTING.CEILING
                  ? classes.perimeterItem
                  : classes.perimeterItemWall
              }
            ></div>
            {fishEyeMounting === FISHEYE_MOUNTING.CEILING && (
              <div
                ref={perimeter2Ref}
                className={classes.perimeterItem}
                style={{ flexBasis: "50%" }}
              ></div>
            )}
          </div>
        )}
        {fishEyeView === FISHEYE_VIEW_TYPES.QUAD && (
          <div data-testid="quad-view" ref={quadRef} className={classes.quadContainer}>
            <div ref={quad1Ref} className={classes.dewarpedContainer}></div>
            <div ref={quad2Ref} className={classes.dewarpedContainer}></div>
            <div ref={quad3Ref} className={classes.dewarpedContainer}></div>
            <div ref={quad4Ref} className={classes.dewarpedContainer}></div>
          </div>
        )}
      </>
    );
  }
);
DewarpViews.displayName = "DewarpViews";
export default DewarpViews;
